// The owner's dashboard at /admin (Ben, 13 Oct 2026): sign-ups, active users and each person's account
// status. Numbers come from admin_stats() (migration 20261013000008), which refuses anyone not in
// app_admins. Account and activity information only: nothing from anyone's ledger.
import { sb } from '../lib/supabase';

/* eslint-disable @typescript-eslint/no-explicit-any */
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const TZ = 'Asia/Singapore';
const fmtDay = (d: string | null) => (d ? new Date(d.slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
const fmtTime = (t: string | null) => (t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: TZ }) : '—');
const ago = (t: string | null) => {
  if (!t) return '';
  const m = (Date.now() - Date.parse(t)) / 60000;
  return m < 1 ? 'just now' : m < 60 ? Math.round(m) + ' min ago' : m < 1440 ? Math.round(m / 60) + ' h ago' : Math.round(m / 1440) + ' d ago';
};
const addDays = (d: string, n: number) => { const x = new Date(d + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };

/** Per-day bars for the last `n` days to `today`, one series in the app's blue, with hover details. */
export function dayBars(rows: { day: string; n: number }[], today: string, n: number, label: string): string {
  const by: Record<string, number> = {}; rows.forEach((r) => { by[r.day] = r.n; });
  const days = Array.from({ length: n }, (_, i) => addDays(today, i - n + 1)), vals = days.map((d) => by[d] || 0);
  const max = Math.max(1, ...vals), top = Math.max(2, Math.ceil(max / 2) * 2), ticks = [0, top / 2, top];
  const W = 560, H = 190, pl = 30, pr = 6, pt = 12, pb = 28, gap = (W - pl - pr) / n, bw = Math.max(3, Math.min(14, gap * 0.62));
  const y = (v: number) => pt + (1 - v / top) * (H - pt - pb);
  let s = `<svg viewBox="0 0 ${W} ${H}" width="100%" role="img" aria-label="${esc(label)}, last ${n} days">`;
  ticks.forEach((v) => { s += `<line class="${v === 0 ? 'axis' : 'grid'}" x1="${pl}" x2="${W - pr}" y1="${y(v)}" y2="${y(v)}"/><text class="lbl" x="${pl - 6}" y="${y(v) + 4}" text-anchor="end">${v}</text>`; });
  days.forEach((d, i) => {
    const cx = pl + i * gap + gap / 2, v = vals[i], h = y(0) - y(v), r = Math.min(3, h);
    s += `<g class="bar" data-tip="${esc(fmtDay(d))}|${v}"><rect x="${pl + i * gap}" y="${pt}" width="${gap}" height="${H - pt - pb}" fill="transparent"/>`;
    if (v > 0) s += `<path d="M${cx - bw / 2},${y(0)} V${y(v) + r} q0,-${r} ${r},-${r} H${cx + bw / 2 - r} q${r},0 ${r},${r} V${y(0)} Z" fill="var(--stock)"/>`;
    s += `</g>`;
    if (i === 0 || i === n - 1 || i === Math.floor((n - 1) / 2)) s += `<text class="lbl" x="${cx}" y="${H - 8}" text-anchor="${i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}">${esc(fmtDay(d).replace(/ \d{4}$/, ''))}</text>`;
  });
  return `<div class="ichart" data-label="${esc(label)}">${s}</svg><div class="itip" hidden></div></div>`;
}

const status = (ok: boolean | null, msg: string | null) => (ok == null ? '<span class="dim">—</span>' : ok ? '✓ OK' : `<span class="neg">⚠ ${esc(msg || 'Failed')}</span>`);

export function adminHtml(s: any): string {
  const t = s.totals, tile = (k: string, v: unknown, sub = '') => `<div class="stat"><div class="k">${k}</div><div class="v">${esc(v)}</div>${sub ? `<div class="dim small">${sub}</div>` : ''}</div>`;
  const firstActive = s.active.length ? s.active[0].day : null;
  const users = s.users.map((u: any) => `<tr>
      <td class="l">${esc(u.email)}${u.admin ? ' <span class="dim small">(you)</span>' : ''}</td>
      <td>${esc(fmtDay(u.joined))}</td>
      <td>${esc(fmtTime(u.last_sign_in))}<span class="sub">${esc(ago(u.last_sign_in))}</span></td>
      <td>${esc(fmtDay(u.last_active))}</td><td>${u.active_days_30}</td>
      <td>${esc(fmtTime(u.last_saved))}<span class="sub">${esc(ago(u.last_saved))}</span></td>
      <td>${u.imported ? '✓ Yes' : '<span class="dim">No</span>'}</td>
      <td class="l">${status(u.hourly_ok, u.hourly_msg)}</td><td>${status(u.dividends_ok, null)}</td></tr>`).join('');
  return `<header class="top"><div class="wrap"><div class="brand"><h1>Portfolio Ledger · Users</h1>
      <div class="hdr-ctl"><div class="status">Updated ${esc(fmtTime(s.generated_at))}</div><button class="btn sm" id="adminRefresh">Refresh</button><a class="btn sm" href="/" style="text-decoration:none">Back to the app</a></div></div></div></header>
    <main class="wrap" style="padding-top:20px;padding-bottom:40px">
    <section class="panel"><div class="panel-head"><h2>Overview</h2></div><div class="stats">
      ${tile('Users', t.users, `${t.new_7d} new in the last 7 days`)}
      ${tile('Active today', t.active_today)}
      ${tile('Active, last 7 days', t.active_7d)}
      ${tile('Active, last 30 days', t.active_30d)}
      ${tile('Brought data from v1', t.imported)}
      ${tile('Hourly values failing', t.hourly_failing, t.hourly_failing ? 'See the table below' : 'All recording')}
    </div></section>
    <div class="grid2" style="margin-top:20px">
      <section class="panel"><div class="panel-head"><h2>Sign-ups, last 30 days</h2></div>${dayBars(s.signups, s.today, 30, 'Sign-ups per day')}</section>
      <section class="panel"><div class="panel-head"><h2>Active users, last 30 days</h2></div>${dayBars(s.active, s.today, 30, 'People who opened the app, per day')}
        <p class="dim small" style="margin-top:8px">People who opened the app each day (Singapore time).${firstActive ? ` Recorded from ${esc(fmtDay(firstActive))}.` : ' Recording starts the next time someone opens the app.'}</p></section>
    </div>
    <section class="panel" style="margin-top:20px"><div class="panel-head"><h2>People</h2></div>
      <div class="scroll"><table><thead><tr><th class="l">Account</th><th>Joined</th><th>Last sign-in</th><th>Last opened</th><th title="Days the app was opened in the last 30 days">Days active</th><th>Last saved</th><th>From v1</th><th class="l">Hourly values</th><th>Dividends</th></tr></thead><tbody>${users}</tbody></table></div>
      <p class="dim small" style="margin-top:10px">Account and activity only: nothing from anyone's ledger is shown here.</p></section>
    </main>`;
}

function wireTips(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('.ichart').forEach((box) => {
    const tip = box.querySelector<HTMLElement>('.itip')!;
    const show = (e: PointerEvent) => {
      const g = (e.target as Element).closest('.bar') as HTMLElement | null;
      box.querySelectorAll('.bar.on').forEach((x) => { if (x !== g) x.classList.remove('on'); });
      if (!g) { tip.hidden = true; return; }
      g.classList.add('on');
      const [d, v] = (g.dataset.tip || '').split('|');
      tip.innerHTML = `<b>${esc(d)}</b><span>${esc(box.dataset.label)}<em>${esc(v)}</em></span>`; tip.hidden = false;
      const r = box.getBoundingClientRect(); let x = e.clientX - r.left + 12; if (x + 200 > r.width) x = e.clientX - r.left - 210;
      tip.style.left = Math.max(0, x) + 'px'; tip.style.top = Math.max(0, e.clientY - r.top - 30) + 'px';
    };
    box.addEventListener('pointermove', show); box.addEventListener('pointerdown', show);
    box.addEventListener('pointerleave', () => { tip.hidden = true; box.querySelectorAll('.bar.on').forEach((x) => x.classList.remove('on')); });
  });
}

export async function renderAdmin(root: HTMLElement) {
  root.innerHTML = '<div class="boot">Loading…</div>';
  const { data, error } = await sb().rpc('admin_stats');
  if (error) {
    const denied = (error as any).code === '42501';
    root.innerHTML = `<main class="auth"><section class="panel auth-card"><h1>Portfolio Ledger</h1><p class="dim">${denied ? 'This page is only for the app’s owner.' : 'Couldn’t load the numbers: ' + esc(error.message)}</p><a class="btn" href="/">Go to the app</a></section></main>`;
    return;
  }
  root.innerHTML = adminHtml(data);
  wireTips(root);
  root.querySelector('#adminRefresh')?.addEventListener('click', () => { void renderAdmin(root); });
}
